import { Approver, DEPARTMENTS, JustificationNote } from 'components/WizardTypes/Wizard.types';

export type TAddRequestAttachments = {
  requestId: number;
  data: any;
};

export type QsRequestedTimeline = {
  qsRequestDetailId: number;
  releaseDate: string | null;
  releaseDateReason: string;
  rowVersion: string;
};

export type RequestTimelineData = {
  requestId: number;
  requestedImplementationTimeline: {
    requestedImplementationDate: string;
    dueDateReason: string;
  } | null;
  ridStateRed: boolean;
  ridStateAmber: boolean;
  escalation?: {
    escalationRequested: boolean;
    escalationApproved: boolean;
    escalationJustification: string;
    escalationApproverTeamId: number;
    escalationApproverName: string;
    escalationRowVersion: string;
  } | null;
  qsRequestedTimeline: QsRequestedTimeline | null;
  rowVersion: string;
};

export type UpdateRequestTimeline = {
  requestId: number;
  requestedImplementationTimeline?: {
    requestedImplementationDate?: string | Date;
    dueDateReason?: string;
  } | null;
  qsRequestedTimeline?: {
    qsRequestDetailId: number;
    /** date-only release month, e.g. "2026-11-01" */
    releaseDate: string;
    releaseDateReason: string;
    rowVersion: string;
  } | null;
  escalation?: {
    escalationApproved?: boolean;
    escalationJustification?: string;
    escalationApproverTeamId?: number;
    escalationRowVersion?: string;
  };
  rowVersion: string;
};

export type AddFDRequestPayload = {
  departmentId: number;
  teamId: number;
  supervisorTeamId: number;
  projectName: string;
  projectPurposeId: number;
  projectPurposeOtherText: string;
  projectTypeId: number;
  updateNotes: string;
  dueDateReason: string;
  intendedUse: string;
  escalationJustification: string;
  escalationApproverTeamId: number;
  draftId: number;
  requestedImplementationDate: string | Date;
  businessImpactId: number;
  justificationNotes: JustificationNote[];
  approvers: Approver[];
};

export type BuildingData = {
  buildings: Array<Building>;
};

export type Building = {
  id: number;
  displayValue: string;
  fdBuildingId: number;
  buildingShortName: string;
  buildingLongName: string;
  groupRoutingId: number;
  groupRoutingTypeId: number;
  groupRoutingName: string;
  orderBy: number;
};

export type AddFDRBuildingPayload = {
  requestId: number;
  fdBuildingId: number;
  floor: string;
  area: string;
  locationText: string;
};

export type GetBuilding = {
  fdDetailBuildingMappingId: number;
  fdRequestDetailId: number;
  fdBuildingId: number;
  floor: string;
  area: string;
  locationText: string;
  rowVersion: string;
};

export type UpdateFDRBuildingPayload = {
  requestId: number;
  fdDetailBuildingMappingId: number;
  fdBuildingId: number;
  floor: string;
  area: string;
  locationText: string;
  rowVersion: string;
};

export type CreateOrResubmitCWRequestPayload = {
  departmentId: number;
  teamId: number;
  supervisorTeamId: number;
  secondaryContactId: number;
  projectName: string;
  projectPurposeId: number;
  projectPurposeOtherText: string;
  projectTypeId: number;
  updateNotes: string;
  dueDateReason: string;
  intendedUse: string;
  escalationJustification: string;
  escalationApproverTeamId: number;
  draftId?: number;
  requestId?: number;
  requestedImplementationDate: Date | null;
  businessImpactId: number;
  justificationNotes: {
    justificationNoteTypeId: number;
    noteText: string;
  }[];
  corporateWebsiteDetail?: {
    brochureOrVideoUpdate: boolean;
    webpages: {
      name: string;
      url: string;
      rowVersion?: string;
      cwWebpageId?: number;
    }[];
    rowVersion?: string;
  };
  requestedRegions: string;
  approvers: {
    sequence: number;
    reviewerId: number;
  }[];
  rowVersion?: string;
};

export type QsSopDocument = {
  documentKey: string;
  documentDescription: string;
  qsDetailSOPDocumentMappingId?: number;
  rowVersion?: string;
};

export type QsRequestDetails = {
  qsRequestDetailId?: number;
  rowVersion?: string;
  sopUpdateTypeId: number;
  auditResponseNumber?: string; // Findings only
  auditResponseId?: number; // Findings only
  capaNumber?: string; // Findings only
  capaId?: number; // Findings only
  gapAnalysisNumber?: string; // Regulatory and Standards Updates only
  gapAnalysisId?: number; // Regulatory and Standards Updates only
  requiresATSSupport: boolean;
  /** Date-only release month; the day is always 01, e.g. "2026-11-01" */
  releaseDate: string | null;
  releaseDateReason: string;
  sopDocuments: QsSopDocument[];
};

export type CreateOrResubmitQSRequestPayload = {
  departmentId: number;
  teamId: number;
  requesterCostCenterNumber: number;
  supervisorTeamId: number;
  secondaryContactId: number;
  projectName: string;
  projectPurposeId: number;
  projectPurposeOtherText: string;
  projectTypeId: number;
  updateNotes: string;
  dueDateReason: string;
  intendedUse: string;
  escalationJustification: string;
  escalationApproverTeamId: number;
  draftId?: number;
  requestId?: number;
  justificationNotes: {
    justificationNoteTypeId: number;
    noteText: string;
  }[];
  qsRequestDetails: QsRequestDetails;
  approvers: {
    sequence: number;
    reviewerId: number;
  }[];
  rowVersion?: string;
};

export type Webpage = {
  cwWebpageId: number;
  cwRequestDetailId: number;
  name: string;
  url: string;
  rowVersion: string;
};

export type CWDetails = {
  cwRequestDetailId: number;
  requestId: number;
  brochureOrVideoUpdate: boolean;
  webpages: Webpage[];
  hasWebpageSection: boolean;
  hasMediaUpdateSection: boolean;
  hasUpdateTypeSection: boolean;
  deleted: boolean;
  rowVersion: string;
};

export type CWWebpage = {
  cwWebpageId: number;
  cwRequestDetailId: number;
  name: string;
  url: string;
  rowVersion: string;
};

export type AddOrEditCWWebpagePayload = {
  requestId: number;
  name: string;
  url: string;
  cwWebpageId?: number;
  rowVersion?: string;
};

export type DeleteCWWebPagePayload = {
  cwWebpageId: number;
  requestId: number;
  rowVersion: string;
};

export type CWMediaSection = {
  cwRequestDetailId: number;
  requestId: number;
  brochureOrVideoUpdate: boolean;
  cwRequestDetailRowVersion: string;
};

export type AtsDetailCategory = {
  id: number;
  displayValue: string;
  name: string;
  description: string;
  orderBy: number;
};

export enum SOP_UPDATE_TYPE {
  Findings = 1,
  ProcessImprovementsAndUpdates = 2,
  RegulatoryAndStandardsUpdates = 3,
  NewMarkets = 4
}

export type SopUpdateType = {
  retired: boolean;
  id: SOP_UPDATE_TYPE;
  displayValue: string;
  name: string;
  description: string;
  orderBy: number;
};

export type QsEnums = {
  sopUpdateTypes: SopUpdateType[];
};

export type AtsApproachFlags = {
  description: string;
  displayValue: string;
  id: number;
  name: string;
  orderBy: number;
};

export type AtsDetailCategories = {
  requestCategories: AtsDetailCategory[];
  requestApproaches: AtsApproachFlags[];
};

export enum RequestViewId {
  MyDashboard = 1,
  Requests = 2,
  Backlog = 3,
  InProcess = 4,
  Tasks = 5,
  Triage = 6,
  Drafts = 7,
  Programs = 8,
  ProgramRequests = 9,
  AssignedRequests = 10,
  TDInProcess = 11,
  TDInQueue = 12,
  TDOnHold = 13,
  TDCompleted = 14
}

export type RequestView = {
  id: RequestViewId;
  displayValue: string;
  name: string;
  description: string;
  orderBy: number;
};

export type UpdateCategories = {
  restrictedCostCenterNumbers: [];
  id: number;
  displayValue: string;
  name: string;
  orderBy: number;
  description: string;
};

export type RequestUpdateCategories = {
  otherCategories: UpdateCategories[];
  recentCategories: UpdateCategories[];
};

export type RequestCategorizationData = {
  requestId: number;
  requestType: {
    purposeId: number;
    purposeText: string;
  };
  documentationDetails: {
    docTypeId: number;
    docTypeText: string;
    rowVersion: string;
  } | null;
  requestUpdateType: {
    projectTypeId: number;
    projectTypeName: string;
    display: boolean;
    required: boolean;
  } | null;
  requestUpdateDetail: {
    updateNotes: string;
    requestUpdateCategoryId: number;
    requestUpdateCategoryName: string;
    requestUpdateCategoryDescription: string;
    rowVersion: string;
  } | null;
  classificiation: {
    classificationId: number;
    classificationName: string;
    display: boolean;
  } | null;
  priority: {
    businessImpactId: number | null;
    businessImpactName: string;
    businessImpactText: string;
  } | null;
  atsDetail: {
    atsRequestCategoryId: number;
    atsRequestCategoryName: string;
    displayATSRequestCategory: boolean;
    atsRequestApproachId: number | null;
    atsRequestApproachName: string;
    displayATSRequestApproach: boolean;
    rowVersion: string;
  } | null;
  qsDetail: QsCategorizationDetail | null;
  rowVersion: string;
};

export type RequestDepartment = {
  id: DEPARTMENTS;
  displayValue: string;
  description: string;
  departmentName: string;
  departmentShortName: string;
  hasSubordinateGroups: boolean;
  orderBy: number;
};

export type RequestDepartmentOption = RequestDepartment & {
  value: DEPARTMENTS;
  name: string;
  label: string;
};

export type IntakeReferenceNumbers = {
  requestId: number;
  qsRequestDetailId: number;
  sopUpdateTypeId: SOP_UPDATE_TYPE;
  sopUpdateTypeName: string;
  auditResponseId: number | null;
  auditResponseNumber: string | null;
  capaId: number | null;
  capaNumber: string | null;
  gapAnalysisId: number | null;
  gapAnalysisNumber: string | null;
  displayAuditResponseNumber: boolean;
  displayCAPANumber: boolean;
  displayGapAnalysisNumber: boolean;
  isIntakeReferenceNumberEditable: boolean;
  rowVersion: string;
};

export type UpdateIntakeReferenceNumbersPayload = {
  requestId: number;
  qsRequestDetailId: number;
  auditResponseId: number | null;
  auditResponseNumber: string | null;
  capaId: number | null;
  capaNumber: string | null;
  gapAnalysisId: number | null;
  gapAnalysisNumber: string | null;
  rowVersion: string;
};

/** PUT /request-management/requests/{requestId}/impacted-area */
export type UpdateImpactedAreaPayload = {
  requestId: number;
  requiresATSSupport: boolean;
  rowVersion: string;
};

export type QsCategorizationSopDocument = {
  qsdetailSopDocumentMappingId: number;
  documentId: string;
  documentDescription: string;
  rowVersion: string;
};

export type QsCategorizationDetail = {
  qsRequestDetailId: number;
  sopUpdateTypeId: SOP_UPDATE_TYPE;
  sopUpdateTypeName: string;
  displaySOPDocuments: boolean;
  isSOPDocumentsRequired: boolean;
  sopDocuments: QsCategorizationSopDocument[] | null;
  isIntakeReferenceNumberEditable: boolean;
  rowVersion: string;
};

export type UpdateRequestCategorizationPayload = {
  purposeId?: number;
  projectTypeId?: number;
  requestUpdateCategoryId?: number | null;
  updateNotes?: string | null;
  classificationId?: number;
  businessImpactId?: number;
  atsRequestCategoryId?: number | null;
  atsRequestApproachId?: number | null;
  rowVersion: string;
};

export type QSFilterDocuments = {
  sopDocuments: [
    {
      documentKey: string;
      documentDescription: string;
    }
  ];
  auditResponses: [
    {
      documentId: number;
      documentName: string;
    }
  ];
  capas: [
    {
      documentId: number;
      documentName: string;
    }
  ];
  gapAnalysis: [
    {
      documentId: number;
      documentName: string;
    }
  ];
};

export type GetJustificationNote = {
  justificationNoteId: number;
  requestId: number;
  justificationNoteTypeId: number;
  justificationNoteWatermark: string;
  justificationNoteLabel: string;
  justificationNoteTypeName: any;
  noteText: string;
  rowVersion: string;
};

export type RequestApprover = {
  sequence: number;
  reviewerId: number;
  reviewerName: string;
  reviewerFirstName: string;
  reviewerLastName: string;
  reviewerPositionTitle: string;
  assignmentId: string | null;
  outcome: string | null;
};

export type ApproverChain = {
  requestId: number;
  status: string;
  requesterId: number;
  requesterName: string;
  approverChain: RequestApprover[];
};

export type FacilitiesDesignDetail = {
  fdRequestDetailId: number;
  requestId: number;
  buildings: Array<BuildingDataDetails>;
  billingCostCenter: number;
  deleted: boolean;
  rowVersion: string;
};

export type BuildingDataDetails = {
  fdDetailBuildingMappingId: number;
  fdRequestDetailId: number;
  fdBuildingId: number;
  floor: string | null;
  area: string | null;
  locationText: string;
  rowVersion: string;
};

export type AtsRequestApproachDTO = {
  display: boolean;
  requestApproachId: number | null;
  requestApproachName: string;
  required: boolean;
};

export type AtsRequestDetail = {
  atsRequestDetailId: number;
  requestId: number;
  atsRequestCategory: {
    atsRequestCategoryId: number | null;
    atsRequestCategoryName: string;
    displayed: boolean;
    required: boolean;
  };
  atsRequestApproachDTO: AtsRequestApproachDTO;
  rowVersion: string;
};

export type RequestUpdateDetail = {
  requestUpdateDetailId: number;
  requestId: number;
  updateNotes: string;
  requestUpdateCategoryId: number;
  requestUpdateCategoryName: string;
  requestUpdateCategoryDescription: string;
  rowVersion: string;
};

export type QsSopDocumentDetail = {
  qsDetailSOPDocumentMappingId: number;
  qsRequestDetailId: number;
  documentKey: string;
  documentDescription: string;
  rowVersion: string;
};

export type QsDetail = {
  qsRequestDetailId: number;
  requestId: number;
  sopUpdateTypeId: SOP_UPDATE_TYPE;
  auditResponseNumber: string | null;
  auditResponseId: number | null;
  capaNumber: string | null;
  capaId: number | null;
  gapAnalysisNumber: string | null;
  gapAnalysisId: number | null;
  requiresATSSupport: boolean;
  /** Date-only release month; the day is always 01, e.g. "2026-11-01" */
  releaseDate: string | null;
  releaseDateReason: string;
  sopDocuments: QsSopDocumentDetail[];
  isIntakeReferenceNumberEditable: boolean;
  rowVersion: string;
};

export type RequestDetails = {
  id: number;
  atsDetail: AtsRequestDetail;
  projectNumber: string;
  projectName: string;
  purposeId: number;
  purposeText: string;
  creatorId: number;
  requester: {
    id: number;
    name: string;
    positionTitle: string;
    managerId: number;
    managerName: string;
    costCenterNumber: number;
    costCenterName: string;
  };
  requestCreator: {
    id: number;
    name: string;
    positionTitle: string;
    managerId: number;
    managerName: string;
    costCenterNumber: number;
    costCenterName: string;
  };
  requesterId: number;
  requesterName: string;
  requestersManagerId: number;
  requestersManagerName: string;
  secondaryContactId: number | null;
  secondaryContactName: string;
  intendedUse: {
    intendedUseText: string;
    displayed: boolean;
  };
  projectTypeId: number;
  projectTypeName: string;
  requestDate: string;
  requestedImplementationDate: string | null;
  plannedImplementationDate: string | null;
  actualImplementationDate: string | null;
  plannedStartDate: string | null;
  actualStartDate: string | null;
  plannedEndDate: string | null;
  actualEndDate: string;
  statusId: number;
  status: string;
  subStatusId: number;
  subStatus: string;
  domainId: number;
  domainName: string;
  departmentId: number;
  departmentName: string;
  projectLeadId: number;
  projectLeadName: string;
  dueDateReason: string;
  qsDetail: QsDetail | null;
  facilitiesDesignDetail: FacilitiesDesignDetail;
  projectReviewerId: number;
  projectReviewerName: string;
  dateLastAuditLog: string;
  lastUpdated: string;
  noPm: boolean;
  hasTasks: boolean;
  classificationId: number;
  classificationName: string;
  requestedRegions: {
    displayed: boolean;
    requestedRegionsText: string;
  };
  requestEvents: any[]; // Replace with the actual type
  deleted: boolean;
  rowVersion: string;
  businessImpact?: {
    businessImpactId: number;
    businessImpactName: string;
    businessImpactText: string;
    displayed: boolean;
    required: boolean;
  };
  requiresValidation: boolean;
  documentationDetails: any; // Replace with the actual type
  requestDetailNotes: any; // Replace with the actual type
  justificationNotes: GetJustificationNote[];
  requestUpdateDetails: RequestUpdateDetail; // Replace with the actual type
  requestSubscriptions: any[]; // Replace with the actual type
  currentUserSubscribed: boolean;
  approverChain: ApproverChain;
  ridStateRed: boolean;
  ridStateAmber: boolean;
  requestSecondaryContact: {
    id: number;
    name: string;
    positionTitle: string;
    managerId: number;
    managerName: string;
    costCenterNumber: number;
    costCenterName: string;
  };
  hasActiveIntegratedTasks: boolean;
};
